/**
 * Actions board — Interventions summary (UX-002 Q1; UX-005 §4.2). Lane-based
 * overview showing count + energy at stake per lane. Figures via Metric; money
 * via the Money component (Value lens only) — never formatted inline, so the
 * state-word discipline can't be bypassed. The intervention lanes follow UX-003
 * §3.6: detected → proposed → awaiting_approval → executing → verifying →
 * closed_verified / closed_unverified.
 *
 * A lane whose count the server did not measure (no lifecycle store exists for
 * anything past `detected`) renders its state word, never 0 and never a dash.
 */
import { type ReactNode } from "react";
import { useGetV1InterventionsSummary } from "@solarkey/api-client";
import type { Figure, Money as MoneyValue } from "@solarkey/api-client";
import {
  Metric,
  Money,
  StateWord,
  Skeleton,
  ErrorState,
  EmptyState,
} from "@solarkey/ui";
import { BackButton } from "../shell/BackButton";

const LANE_LABEL: Record<string, string> = {
  detected: "Detected",
  proposed: "Proposed",
  awaiting_approval: "Awaiting approval",
  executing: "Executing",
  verifying: "Verifying",
  closed_verified: "Closed (verified)",
  closed_unverified: "Closed (unverified)",
};

export function InterventionsScreen(props: {
  scope?: string;
}): ReactNode {
  const query = useGetV1InterventionsSummary({ scope: props.scope });

  if (query.isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <BackButton />
        <h1 style={h1}>Actions</h1>
      </div>
        <div style={board}>
          <Skeleton variant="tile" />
          <Skeleton variant="tile" />
          <Skeleton variant="tile" />
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load the action board"
        onRetry={() => void query.refetch()}
      />
    );
  }

  const lanes = query.data.data.data?.lanes ?? [];
  // Verified-quiet is the server's statement (meta.quiet), never inferred from an
  // empty board — an empty board can equally mean nothing was checked (UX-003
  // principle 5: the two empty states are never interchangeable).
  const quiet = query.data.data.meta?.quiet;
  const isVerifiedQuiet = quiet?.is_quiet === true;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <BackButton />
        <h1 style={h1}>Actions</h1>
      </div>

      {lanes.length === 0 ? (
        isVerifiedQuiet ? (
          <EmptyState
            variant="verified_quiet"
            title="No interventions"
            message={quiet?.text ?? "Checked, and nothing needs attention."}
          />
        ) : (
          <EmptyState
            variant="no_data"
            title="Not measured"
            message="The action board returned no lanes, and the server made no verified-quiet statement — so this is an absence of data, not a confirmed all-clear."
          />
        )
      ) : (
        <div style={board}>
          {lanes.map((l, i) => (
            <LaneCard
              key={l.lane ?? i}
              lane={l.lane ?? "unknown"}
              // A count the server did not measure stays absent. `?? 0` here was
              // the invented zero: it asserted an empty lane the platform cannot see.
              count={typeof l.count === "number" ? l.count : null}
              energyAtStake={l.energy_at_stake}
              moneyAtStake={l.money_at_stake}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LaneCard(props: {
  lane: string;
  count: number | null;
  energyAtStake?: Figure;
  moneyAtStake?: MoneyValue;
}): ReactNode {
  const isOpen = !props.lane.startsWith("closed");

  return (
    <div style={{ ...laneCard, borderLeftColor: isOpen ? "var(--sk-color-accent)" : "var(--sk-color-border-2)" }}>
      <div style={laneHeader}>
        <span style={laneName}>{LANE_LABEL[props.lane] ?? props.lane}</span>
        {props.count === null ? (
          // The lane's own reason travels on its figure — the server states why
          // the lane cannot be counted, the screen does not guess at it.
          <StateWord
            state="NOT_MEASURED"
            reason={props.energyAtStake?.reason ?? null}
          />
        ) : (
          <span style={laneCount}>{props.count}</span>
        )}
      </div>

      {props.energyAtStake ? (
        <div style={laneMetric}>
          <span style={metricLabel}>Energy at stake</span>
          <Metric figure={props.energyAtStake} showFreshness={false} />
        </div>
      ) : null}

      {props.moneyAtStake ? (
        <div style={laneMetric}>
          <span style={metricLabel}>Value at stake</span>
          {/* Money renders amount, currency and state itself; formatting it inline
              here is what let a hardcoded dash stand in for a withheld value. */}
          <Money money={props.moneyAtStake} />
        </div>
      ) : null}
    </div>
  );
}

const h1: React.CSSProperties = {
  margin: 0,
  font: "600 20px/1.25 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
};
const board: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gap: 12,
};
const laneCard: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 12,
  borderRadius: "var(--sk-radius-card)",
  border: "1px solid var(--sk-color-border-1)",
  borderLeft: "3px solid var(--sk-color-accent)",
  background: "var(--sk-color-surface-1)",
};
const laneHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};
const laneName: React.CSSProperties = {
  font: "500 12px/1.35 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
};
const laneCount: React.CSSProperties = {
  font: "600 20px/1.15 var(--sk-font-mono)",
  color: "var(--sk-color-ink-1)",
};
const laneMetric: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};
const metricLabel: React.CSSProperties = {
  font: "600 9px/1.3 var(--sk-font-text)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--sk-color-ink-3)",
};
